import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const AdminContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: linear-gradient(135deg, #0c162d 0%, #122240 50%, #0a192f 100%);
  color: #c4cdda;
`;

const Sidebar = styled.div`
  width: 250px;
  background: rgba(18, 34, 64, 0.8);
  border-right: 1px solid rgba(0, 194, 255, 0.2);
  padding: 20px 0;
  box-shadow: 3px 0 15px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    width: 50px;
  }
`;

const SidebarHeader = styled.div`
  padding: 0 20px 20px;
  border-bottom: 1px solid rgba(0, 194, 255, 0.2);
  margin-bottom: 20px;
  
  h2 {
    color: #64ffda;
    margin: 0;
    font-size: 1.5rem;
  }
`;

const SidebarMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MenuItem = styled.li<{ active?: boolean }>`
  padding: 15px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  border-left: 3px solid ${props => props.active ? '#00c2ff' : 'transparent'};
  
  &:hover {
    background: rgba(0, 194, 255, 0.1);
  }
  
  ${props => props.active && `
    background: rgba(0, 194, 255, 0.1);
  `}
  
  span {
    margin-left: 10px;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    padding: 15px 0;
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(0, 194, 255, 0.2);
`;

const WelcomeText = styled.div`
  h1 {
    margin: 0;
    color: #ffffff;
    font-size: 2rem;
  }
  
  p {
    margin: 5px 0 0;
    color: #8892b0;
  }
`;

const LogoutButton = styled.button`
  background: rgba(255, 107, 107, 0.2);
  color: #ff6b6b;
  border: 1px solid rgba(255, 107, 107, 0.5);
  padding: 10px 20px;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: rgba(255, 107, 107, 0.3);
    transform: translateY(-2px);
  }
`;

const SectionContent = styled.div`
  h2 {
    color: #ffffff;
    margin-top: 0;
  }
  
  p {
    line-height: 1.6;
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 25px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const DashboardCard = styled.div`
  background: rgba(18, 34, 64, 0.6);
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 194, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 35px rgba(0, 194, 255, 0.4);
    border-color: rgba(0, 194, 255, 0.5);
  }
  
  h3 {
    margin-top: 0;
    color: #64ffda;
    display: flex;
    align-items: center;
    
    span {
      margin-right: 10px;
      font-size: 1.5rem;
    }
  }
`;

const UserGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const UserCard = styled.div`
  background: rgba(18, 34, 64, 0.4);
  border-radius: 10px;
  padding: 20px;
  border: 1px solid rgba(0, 194, 255, 0.1);
  
  h4 {
    margin: 0 0 10px 0;
    color: #64ffda;
  }
  
  .info {
    font-size: 0.9rem;
    color: #a8b3cf;
    margin: 5px 0;
  }
  
  .actions {
    display: flex;
    gap: 10px;
    margin-top: 15px;
  }
  
  button {
    flex: 1;
    padding: 8px;
    border-radius: 5px;
    border: none;
    cursor: pointer;
    font-weight: 500;
  }
  
  .edit {
    background: rgba(0, 194, 255, 0.2);
    color: #64ffda;
  }
  
  .delete {
    background: rgba(255, 107, 107, 0.2);
    color: #ff6b6b;
  }
`;

const SettingsForm = styled.form`
  background: rgba(18, 34, 64, 0.4);
  border-radius: 10px;
  padding: 20px;
  border: 1px solid rgba(0, 194, 255, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 8px;
    color: #c4cdda;
    font-weight: 500;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 12px 15px;
    border-radius: 8px;
    border: 1px solid rgba(0, 194, 255, 0.3);
    background: rgba(18, 34, 64, 0.6);
    color: #ffffff;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: #00c2ff;
      box-shadow: 0 0 0 2px rgba(0, 194, 255, 0.2);
    }
  }
`;

const SaveButton = styled.button`
  background: linear-gradient(90deg, #00c2ff, #64ffda);
  color: #0c162d;
  border: none;
  padding: 12px 30px;
  border-radius: 30px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 194, 255, 0.4);
  }
`;

const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();

  // Check if admin is authenticated
  useEffect(() => {
    const isAdminAuthenticated = localStorage.getItem('isAdminAuthenticated') === 'true';
    
    if (!isAdminAuthenticated) {
      // Redirect to admin login page if not authenticated
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    // Clear admin authentication state
    localStorage.removeItem('isAdminAuthenticated');
    // Redirect to home page
    navigate('/');
  };

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'users', label: 'User Management', icon: '👥' },
    { id: 'brokers', label: 'Broker Management', icon: '🏦' },
    { id: 'transactions', label: 'Transactions', icon: '💸' },
    { id: 'content', label: 'Content Management', icon: '📄' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'support', label: 'Support Tickets', icon: '🎧' },
    { id: 'settings', label: 'System Settings', icon: '⚙️' },
  ];

  // Mock data for demonstration
  const mockUsers = [
    { id: 1, name: 'John Doe', email: 'john@example.com', status: 'Active', role: 'user', joinDate: '2023-01-15' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', status: 'Active', role: 'user', joinDate: '2023-02-20' },
    { id: 3, name: 'Robert Johnson', email: 'robert@example.com', status: 'Inactive', role: 'user', joinDate: '2023-03-10' },
  ];

  const handleViewUser = (user: any) => {
    alert(`Viewing details for user: ${user.name}`);
  };

  const handleEditUser = (user: any) => {
    alert(`Editing user: ${user.name}`);
  };

  const handleDeleteUser = (userId: number, userName: string) => {
    if (window.confirm(`Are you sure you want to delete user ${userName}?`)) {
      alert(`User ${userName} has been deleted.`);
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <SectionContent>
            <h2>Admin Dashboard Overview</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>👥</span> Total Users</h3>
                <div className="value">1,248</div>
                <div className="change">+12% this month</div>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>🏦</span> Active Brokers</h3>
                <div className="value">12</div>
                <div className="change">2 new this week</div>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>💸</span> Total Transactions</h3>
                <div className="value">3,421</div>
                <div className="change">+8% this month</div>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>🎫</span> Support Tickets</h3>
                <div className="value">24</div>
                <div className="change">5 pending</div>
              </DashboardCard>
            </DashboardGrid>
            
            <DashboardCard>
              <h3><span>🔔</span> Recent Activity</h3>
              <ul>
                <li>New user registration - John Doe</li>
                <li>Broker connection established - Exness</li>
                <li>Support ticket submitted - Issue with login</li>
                <li>Content updated - Homepage banner</li>
              </ul>
            </DashboardCard>
          </SectionContent>
        );
      
      case 'users':
        return (
          <SectionContent>
            <h2>User Management</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>👥</span> User List</h3>
                <UserGrid>
                  {mockUsers.map((user) => (
                    <UserCard key={user.id}>
                      <h4>{user.name}</h4>
                      <div className="info">{user.email}</div>
                      <div className="info">Status: {user.status}</div>
                      <div className="info">Role: {user.role}</div>
                      <div className="info">Joined: {user.joinDate}</div>
                      <div className="actions">
                        <button className="edit" onClick={() => handleViewUser(user)}>View</button>
                        <button className="edit" onClick={() => handleEditUser(user)}>Edit</button>
                        <button className="delete" onClick={() => handleDeleteUser(user.id, user.name)}>Delete</button>
                      </div>
                    </UserCard>
                  ))}
                </UserGrid>
              </DashboardCard>
              
              <DashboardCard>
                <h3><span>➕</span> Add New User</h3>
                <SettingsForm>
                  <FormGroup>
                    <label htmlFor="userName">Full Name</label>
                    <input type="text" id="userName" placeholder="Enter full name" />
                  </FormGroup>
                  
                  <FormGroup>
                    <label htmlFor="userEmail">Email Address</label>
                    <input type="email" id="userEmail" placeholder="Enter email" />
                  </FormGroup>
                  
                  <FormGroup>
                    <label htmlFor="userPassword">Password</label>
                    <input type="password" id="userPassword" placeholder="Enter password" />
                  </FormGroup>
                  
                  <SaveButton>Add User</SaveButton>
                </SettingsForm>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      case 'brokers':
        return (
          <SectionContent>
            <h2>Broker Management</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>🏦</span> Broker List</h3>
                <p>Broker management functionality would go here.</p>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      case 'transactions':
        return (
          <SectionContent>
            <h2>Transaction Management</h2>
            <DashboardCard>
              <h3><span>💸</span> Recent Transactions</h3>
              <p>Transaction data will be displayed here.</p>
            </DashboardCard>
          </SectionContent>
        );
      
      case 'content':
        return (
          <SectionContent>
            <h2>Content Management</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>📄</span> Edit Homepage Content</h3>
                <SettingsForm>
                  <FormGroup>
                    <label htmlFor="heroTitle">Hero Title</label>
                    <input type="text" id="heroTitle" defaultValue="UPM Traders" />
                  </FormGroup>
                  
                  <SaveButton>Update Content</SaveButton>
                </SettingsForm>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      case 'analytics':
        return (
          <SectionContent>
            <h2>Analytics Dashboard</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>📈</span> User Growth</h3>
                <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                  <p>Chart visualization would go here</p>
                </div>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      case 'support':
        return (
          <SectionContent>
            <h2>Support Ticket Management</h2>
            <DashboardCard>
              <h3><span>🎫</span> Open Tickets</h3>
              <p>Support ticket data will be displayed here.</p>
            </DashboardCard>
          </SectionContent>
        );
      
      case 'settings':
        return (
          <SectionContent>
            <h2>System Settings</h2>
            <DashboardGrid>
              <DashboardCard>
                <h3><span>⚙️</span> Platform Settings</h3>
                <SettingsForm>
                  <FormGroup>
                    <label htmlFor="platformName">Platform Name</label>
                    <input type="text" id="platformName" defaultValue="UPM Traders" />
                  </FormGroup>
                  
                  <SaveButton>Save Settings</SaveButton>
                </SettingsForm>
              </DashboardCard>
            </DashboardGrid>
          </SectionContent>
        );
      
      default:
        return (
          <SectionContent>
            <h2>Welcome to UPM Traders Admin Dashboard</h2>
            <p>Select a section from the sidebar to get started.</p>
          </SectionContent>
        );
    }
  };

  return (
    <AdminContainer>
      <Sidebar>
        <SidebarHeader>
          <h2>Admin Panel</h2>
        </SidebarHeader>
        <SidebarMenu>
          {menuItems.map((item) => (
            <MenuItem 
              key={item.id} 
              active={activeSection === item.id}
              onClick={() => setActiveSection(item.id)}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </MenuItem>
          ))}
          <MenuItem onClick={handleLogout}>
            <span>🚪</span>
            <span>Logout</span>
          </MenuItem>
        </SidebarMenu>
      </Sidebar>
      
      <MainContent>
        <Header>
          <WelcomeText>
            <h1>Admin Dashboard</h1>
            <p>Welcome back, Administrator</p>
          </WelcomeText>
          <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
        </Header>
        
        {renderSection()}
      </MainContent>
    </AdminContainer>
  );
};

export default AdminDashboard;